import { useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { uploadTranscript } from "@/features/transcripts/api";
import type { ForWho } from "@/features/transcripts/types";
import { ApiError } from "@/lib/api";

interface Props {
  clientId: string;
  forWho: ForWho;
  /** Fires after a successful parse so the pages refetch. */
  onUploaded: () => void;
}

export function UploadTranscriptDialog({ clientId, forWho, onUploaded }: Props) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!file) return;
    setSubmitting(true);
    try {
      const summary = await uploadTranscript(clientId, file, forWho);
      const parts = [
        summary.created && `${summary.created} added`,
        summary.updated && `${summary.updated} updated`,
        summary.skipped && `${summary.skipped} manual row(s) preserved`,
      ].filter(Boolean);
      toast.success(`Parsed ${summary.type} transcript${parts.length ? ` — ${parts.join(", ")}` : ""}.`);
      onUploaded();
      setFile(null);
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Upload /> Upload Transcript
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Transcript</DialogTitle>
          <DialogDescription>
            Upload an IRS transcript HTML file downloaded manually from e-Services. The transcript's
            ITIN must match this client's on file.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="transcript-file">Transcript file</Label>
          <Input
            id="transcript-file"
            type="file"
            accept=".html,.htm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <DialogFooter>
          <Button type="button" disabled={!file || submitting} onClick={handleSubmit}>
            {submitting ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
